/**
 * Help center articles the app links to at the moment a traveller needs them (a declined card,
 * a cancellation, a refused change) and suggests in the Help Scout Beacon per page.
 *
 * The articles live in `help-docs/` and are published to the Just Good Travel help center, one
 * site for both brands. Help Scout addresses an article by its number: `/article/32-anything`
 * resolves, `/article/payment-declined` is a 404. So each slug carries the number Help Scout gave
 * it on first publish (from help.justgoodtravel.com/sitemap.xml). Numbers never change once given;
 * a new article needs its number added here after its first publish, and a renamed slug keeps
 * its number (the old URL redirects).
 */
export const HELP_CENTER_URL = 'https://help.justgoodtravel.com';

export const HELP_ARTICLES = {
   'add-a-night': 19,
   'cancel-a-booking': 20,
   'change-a-booking': 21,
   'check-in-basics': 22,
   'choose-or-change-organization': 23,
   'confirmation-and-receipts': 24,
   'contact-us': 25,
   'coupons-and-discounts': 26,
   'for-organizations': 27,
   'how-proceeds-work': 28,
   'how-to-book': 29,
   'multiple-rooms-and-guests': 30,
   'my-trips': 31,
   'payment-declined': 32,
   'payment-methods-and-saved-cards': 33,
   'price-alerts-and-emails': 34,
   'problem-at-the-hotel': 35,
   'rate-types': 36,
   'season-planner': 37,
   'sign-in-and-account': 38,
   'whats-included-in-your-rate': 39,
} as const;

export type HelpArticleSlug = keyof typeof HELP_ARTICLES;

export const helpArticleUrl = (slug: HelpArticleSlug): string => `${HELP_CENTER_URL}/article/${HELP_ARTICLES[slug]}-${slug}`;

/** en.json key for an article's short link text, e.g. `helpArticles.payment-declined`. */
export const helpArticleLabelKey = (slug: HelpArticleSlug): string => `helpArticles.${slug}`;

/**
 * Articles the Beacon suggests on each page, most useful first. The first matching rule wins; a
 * page with no rule gets Help Scout's own suggestions back. Admin pages never load the Beacon.
 */
const BEACON_SUGGESTION_RULES: Array<{ match: RegExp; articles: HelpArticleSlug[] }> = [
   { match: /^\/c\/[^/]+\/book\/?$/, articles: ['payment-declined', 'rate-types', 'payment-methods-and-saved-cards', 'coupons-and-discounts', 'multiple-rooms-and-guests'] },
   { match: /^\/c\/[^/]+\/plan(\/|$)/, articles: ['season-planner', 'how-to-book', 'rate-types'] },
   { match: /^\/c\/[^/]+\/hotels(\/|$)/, articles: ['how-to-book', 'rate-types', 'whats-included-in-your-rate', 'coupons-and-discounts', 'multiple-rooms-and-guests'] },
   // NPT-2263 — the change wizard.
   { match: /^\/my-trips\/[^/]+\/change\/?$/, articles: ['change-a-booking', 'rate-types', 'cancel-a-booking', 'payment-declined', 'add-a-night'] },
   { match: /^\/my-trips\/[^/]+\/?$/, articles: ['change-a-booking', 'cancel-a-booking', 'add-a-night', 'check-in-basics', 'problem-at-the-hotel', 'confirmation-and-receipts'] },
   { match: /^\/my-trips\/?$/, articles: ['my-trips', 'confirmation-and-receipts', 'change-a-booking', 'cancel-a-booking'] },
   { match: /^\/payment-methods\/?$/, articles: ['payment-methods-and-saved-cards', 'payment-declined'] },
   { match: /^\/my-price-tracking\/?$/, articles: ['price-alerts-and-emails'] },
   { match: /^\/(my-account|profile|account\/disabled)(\/|$)/, articles: ['sign-in-and-account', 'price-alerts-and-emails'] },
   { match: /^\/c\/[^/]+\/?$/, articles: ['how-proceeds-work', 'choose-or-change-organization', 'for-organizations', 'how-to-book'] },
   { match: /^\/c\/?$/, articles: ['choose-or-change-organization', 'how-proceeds-work'] },
];

export const getBeaconSuggestionSlugs = (pathname: string): HelpArticleSlug[] | null =>
   BEACON_SUGGESTION_RULES.find(rule => rule.match.test(pathname))?.articles ?? null;
